import { request } from '@/api/http';

export interface AdminBillingDashboard {
  cards: Record<string, number | string | null>;
  charts: {
    subscriptionsByPlan: { planSlug: string; planName: string; count: number }[];
    revenueOverTime: { month: string; revenueCents: number; paymentCount: number }[];
    subscriptionLifecycle: { status: string; count: number }[];
    organizationGrowth: { month: string; count: number }[];
  };
  recentPayments: Record<string, unknown>[];
  recentActivity: Record<string, unknown>[];
  cachedAt?: string;
}

export interface AdminBillingCursorList<T = Record<string, unknown>> {
  items: T[];
  nextCursor?: string | null;
  hasMore: boolean;
}

export interface AdminBillingRevenue {
  mrrCents: number;
  arrCents: number;
  refundsThisMonthCents: number;
  revenueByMonth: { month: string; revenueCents: number }[];
  revenueByPlan: { planSlug: string; planName: string; mrrCents: number; subscriptions: number }[];
  planDistribution: { planSlug: string; planName: string; count: number }[];
  organizationGrowth: { month: string; count: number }[];
  conversionRatePercent: number;
}

export interface AdminBillingOrganization {
  organization: Record<string, unknown>;
  health: Record<string, unknown>;
  subscription: Record<string, unknown>;
  entitlements: Record<string, unknown>[];
  payments: Record<string, unknown>[];
  providerCustomers: Record<string, unknown>[];
  auditHistory: Record<string, unknown>[];
}

export interface AdminBillingAttention {
  paymentFailed: Record<string, unknown>[];
  graceEnding: Record<string, unknown>[];
  expiringTomorrow: Record<string, unknown>[];
  expiringSoon: Record<string, unknown>[];
  expired: Record<string, unknown>[];
  manualGrants: Record<string, unknown>[];
  webhookFailures: Record<string, unknown>[];
  providerSyncErrors: Record<string, unknown>[];
  quotaAbuse: Record<string, unknown>[];
  organizationDisabled: Record<string, unknown>[];
  failedReminders: Record<string, unknown>[];
}

function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

export const adminBillingService = {
  dashboard: (refresh = false) =>
    request<AdminBillingDashboard>(`/admin/billing/dashboard${refresh ? '?refresh=true' : ''}`),

  revenue: () => request<AdminBillingRevenue>('/admin/billing/revenue'),

  subscriptions: (params: Record<string, string | number | boolean | undefined> = {}) =>
    request<AdminBillingCursorList>(`/admin/billing/subscriptions${qs(params)}`),

  payments: (params: Record<string, string | number | boolean | undefined> = {}) =>
    request<AdminBillingCursorList>(`/admin/billing/payments${qs(params)}`),

  plans: (includeInactive = true) =>
    request<{ items: Record<string, unknown>[] }>(
      `/admin/billing/plans?includeInactive=${includeInactive}`
    ),

  providers: () => request<{ items: Record<string, unknown>[] }>('/admin/billing/providers'),

  webhooks: (params: Record<string, string | number | boolean | undefined> = {}) =>
    request<AdminBillingCursorList>(`/admin/billing/webhooks${qs(params)}`),

  webhookDetail: (id: string) =>
    request<Record<string, unknown>>(`/admin/billing/webhooks/${id}`),

  replayWebhook: (id: string) =>
    request<{ status: string }>(`/admin/billing/webhooks/${id}/replay`, { method: 'POST' }),

  attention: () => request<AdminBillingAttention>('/admin/billing/attention'),

  dismissAttention: (body: { attentionKey: string; organizationId: string; comment?: string }) =>
    request<{ status: string }>('/admin/billing/attention/dismiss', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  organizations: (params: Record<string, string | number | boolean | undefined> = {}) =>
    request<AdminBillingCursorList>(`/admin/billing/organizations${qs(params)}`),

  organization: (id: string) =>
    request<AdminBillingOrganization>(`/admin/billing/organizations/${id}`),

  grant: (
    organizationId: string,
    body: { planSlug?: string; planId?: string; expiresAt?: string; reason?: string },
  ) =>
    request<Record<string, unknown>>(`/admin/billing/organizations/${organizationId}/grant`, {
      method: 'POST',
      body: JSON.stringify({ organizationId, ...body }),
    }),

  revoke: (organizationId: string, entitlementId: string, reason?: string) =>
    request<{ status: string }>(
      `/admin/billing/organizations/${organizationId}/revoke?entitlementId=${entitlementId}${reason ? `&reason=${encodeURIComponent(reason)}` : ''}`,
      { method: 'POST' }
    ),

  resetUsage: (
    organizationId: string,
    body: {
      resetSearchUsage: boolean;
      resetNotificationUsage: boolean;
      resetJobUsage: boolean;
      reason: string;
    },
  ) =>
    request<{ organizationId: string; resetFields: string[]; usage: Record<string, unknown> }>(
      `/admin/organizations/${organizationId}/usage/reset`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  manualOp: (body: Record<string, unknown>) =>
    request<Record<string, unknown>>('/admin/billing/manual-ops', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  audit: (cursor?: string) =>
    request<AdminBillingCursorList>(`/admin/billing/audit${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`),

  exportReport: (reportType: string, fmt: 'csv' | 'json' = 'csv') =>
    `/api/admin/billing/reports/${reportType}?fmt=${fmt}`,
};

export function formatCents(cents: number | null | undefined, currency = 'EUR'): string {
  if (cents == null) return '—';
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
}
